/**
 * Uma query por relatório (§3.6 da v4). Filtros entram como parâmetros e
 * fazem parte da queryKey; arrays vazios viram undefined para não sujar a
 * URL (o api-client junta arrays com vírgula, formato do intList do shared).
 */
import { queryOptions } from '@tanstack/react-query';
import { api, apiDownload } from '@/lib/api-client';
import {
  extractPdfFileName,
  type AppointmentsExtractReport,
  type AttendantsReport,
  type CatalogReport,
  type ExecutiveReport,
  type PeaksReport,
  type RatingsReport,
  type ReportFilterOptions,
  type WorkloadReport,
} from '@erp/shared';

export interface ReportPeriod {
  /** ISO date YYYY-MM-DD */
  start: string;
  end: string;
}

/** recorte pelo cliente do ticket (PoolTrack, 02/10) — todo relatório aceita */
export interface ClientScoped {
  clientIds?: number[];
}

/** período + cliente: peaks e catálogo */
export interface ClientFilteredParams extends ReportPeriod, ClientScoped {}

/** Linhas por página no extrato (a API aceita até 200). */
export const EXTRACT_PAGE_SIZE = 50;

export interface AppointmentsExtractParams extends ClientFilteredParams {
  userIds?: number[];
  deskIds?: number[];
  /** página (1…); ausente = TODAS as linhas — é o que a exportação pede */
  page?: number;
}

export interface DeskFilteredParams extends ClientFilteredParams {
  deskIds?: number[];
}

/** [] ⇒ undefined (sem filtro). */
function ids(list: number[] | undefined): number[] | undefined {
  return list && list.length > 0 ? list : undefined;
}

async function fetchReport<T>(
  path: string,
  query: Record<string, string | number | number[] | undefined>,
  signal: AbortSignal
): Promise<T> {
  const res = await api<T>(path, { query, signal });
  if (res.error) throw res.error;
  return res.data;
}

/**
 * 1. GET /reports/appointments-extract — com `page`, só aquela página (+ o
 * rowsTotal); os totais vêm sempre do período inteiro.
 */
export function appointmentsExtractReportQuery(params: AppointmentsExtractParams) {
  const paged = params.page !== undefined;
  return queryOptions({
    queryKey: ['erp', 'reports', 'appointments-extract', params] as const,
    queryFn: ({ signal }) =>
      fetchReport<AppointmentsExtractReport>(
        '/reports/appointments-extract',
        {
          start: params.start,
          end: params.end,
          user_ids: ids(params.userIds),
          desk_ids: ids(params.deskIds),
          client_ids: ids(params.clientIds),
          limit: paged ? EXTRACT_PAGE_SIZE : undefined,
          offset: paged ? ((params.page ?? 1) - 1) * EXTRACT_PAGE_SIZE : undefined,
        },
        signal
      ),
  });
}

/**
 * O extrato em PDF (PoolTrack, 03/10: "baixar em PDF o relatório de cada
 * atendente"): o MESMO filtro com `format=pdf`, sempre o período inteiro (sem
 * página). `attendantName` dá nome ao arquivo quando o documento é de uma
 * pessoa só — a mesma regra da API (extractPdfFileName, no shared).
 *
 * `expectType`: a API anterior a esta mudança ignora `format` e responde JSON;
 * sem a guarda a pessoa salvaria um ".pdf" que não abre.
 */
export function downloadAppointmentsExtractPdf(
  params: Omit<AppointmentsExtractParams, 'page'> & { attendantName?: string | null }
): Promise<void> {
  const query = new URLSearchParams({ start: params.start, end: params.end, format: 'pdf' });
  const lists: [string, number[] | undefined][] = [
    ['user_ids', ids(params.userIds)],
    ['desk_ids', ids(params.deskIds)],
    ['client_ids', ids(params.clientIds)],
  ];
  for (const [key, list] of lists) if (list) query.set(key, list.join(','));
  return apiDownload(
    `/reports/appointments-extract?${query.toString()}`,
    extractPdfFileName({
      start: params.start,
      end: params.end,
      attendantName: params.attendantName,
    }),
    { expectType: 'application/pdf' }
  );
}

/** 2. GET /reports/workload — foto de agora, sem período; só o recorte de cliente. */
export function workloadReportQuery(params: ClientScoped = {}) {
  return queryOptions({
    queryKey: ['erp', 'reports', 'workload', params] as const,
    queryFn: ({ signal }) =>
      fetchReport<WorkloadReport>(
        '/reports/workload',
        { client_ids: ids(params.clientIds) },
        signal
      ),
  });
}

/** 3. GET /reports/peaks */
export function peaksReportQuery(params: ClientFilteredParams) {
  return queryOptions({
    queryKey: ['erp', 'reports', 'peaks', params] as const,
    queryFn: ({ signal }) =>
      fetchReport<PeaksReport>(
        '/reports/peaks',
        { start: params.start, end: params.end, client_ids: ids(params.clientIds) },
        signal
      ),
  });
}

/** 4. GET /reports/executive */
export function executiveReportQuery(params: DeskFilteredParams) {
  return queryOptions({
    queryKey: ['erp', 'reports', 'executive', params] as const,
    queryFn: ({ signal }) =>
      fetchReport<ExecutiveReport>(
        '/reports/executive',
        {
          start: params.start,
          end: params.end,
          desk_ids: ids(params.deskIds),
          client_ids: ids(params.clientIds),
        },
        signal
      ),
  });
}

/** 5. GET /reports/catalog */
export function catalogReportQuery(params: ClientFilteredParams) {
  return queryOptions({
    queryKey: ['erp', 'reports', 'catalog', params] as const,
    queryFn: ({ signal }) =>
      fetchReport<CatalogReport>(
        '/reports/catalog',
        { start: params.start, end: params.end, client_ids: ids(params.clientIds) },
        signal
      ),
  });
}

/** 6. GET /reports/ratings */
export function ratingsReportQuery(params: DeskFilteredParams) {
  return queryOptions({
    queryKey: ['erp', 'reports', 'ratings', params] as const,
    queryFn: ({ signal }) =>
      fetchReport<RatingsReport>(
        '/reports/ratings',
        {
          start: params.start,
          end: params.end,
          desk_ids: ids(params.deskIds),
          client_ids: ids(params.clientIds),
        },
        signal
      ),
  });
}

export interface AttendantsParams extends ClientFilteredParams {
  userIds?: number[];
}

/** 7. GET /reports/attendants (spec 015) */
export function attendantsReportQuery(params: AttendantsParams) {
  return queryOptions({
    queryKey: ['erp', 'reports', 'attendants', params] as const,
    queryFn: ({ signal }) =>
      fetchReport<AttendantsReport>(
        '/reports/attendants',
        {
          start: params.start,
          end: params.end,
          user_ids: ids(params.userIds),
          client_ids: ids(params.clientIds),
        },
        signal
      ),
  });
}

/**
 * GET /reports/filter-options — a equipe do tenant (nunca o login do portal)
 * e os clientes, para os seletores. Muda pouco: um minuto de cache.
 */
export function reportFilterOptionsQuery() {
  return queryOptions({
    queryKey: ['erp', 'reports', 'filter-options'] as const,
    queryFn: ({ signal }) =>
      fetchReport<ReportFilterOptions>('/reports/filter-options', {}, signal),
    staleTime: 60_000,
  });
}
